import React, {
  ReactNode,
  createContext,
  useContext,
  useMemo,
  useState
} from 'react';

import { useDentist } from '@/contexts/DentistContext';
import {
  decodeWeeklyReportData,
  getLatestWeeklyReportDate,
  getWeeklyReportByDateAndDentistId,
  getWeeklyReportDates
} from '@/data/WeeklyReports';
import { useToast } from '@/hooks';
import { WEEKLY_REPORT_DEFAULT_DENTIST } from '@/utils/constants';
import { now } from '@/utils/helpers/now';
import { parseDate } from '@/utils/helpers/parseDate';
import { AppointmentsByDay, WeeklyReportData } from '@/utils/types';
import {
  UseQueryResult,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import { addDays, compareDesc, format, startOfWeek } from 'date-fns';

interface WeeklyReportsContextType {
  latestStartDate: string | null;
  selectedStartDate: string | null;
  setSelectedStartDate: (date: string | null) => void;
  selectedDentist: string | null;
  setSelectedDentist: (dentist: string | null) => void;
  dentists: string[];
  weeklyReportData: WeeklyReportData | null;
  loading: boolean;
  loaded: boolean;
  appointmentsPerDay: AppointmentsByDay;
  totalAppointments: number;
  noShowRate: number;
  lateRate: number;
  startDates: string[];
  loadingStartDates: boolean;
  loadedStartDates: boolean;
  fetchStartDates: () => void;
  chartData: { Day: string; Appointments: number }[];
  noShowPatients: string[];
  latePatients: string[];
}

interface WeeklyReportQueryResult {
  data: WeeklyReportData;
  dentists: string[];
}

const WeeklyReportsContext = createContext<
  WeeklyReportsContextType | undefined
>(undefined);

export const WeeklyReportsProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const displayToast = useToast();
  const { selectedDentist: dentist } = useDentist();
  const queryClient = useQueryClient();
  const dentistId = dentist?.id;

  const [selectedStartDate, setSelectedStartDate] = useState<string | null>(
    null
  );
  const [selectedDentist, setSelectedDentist] = useState<string | null>(null);

  const { data: latestStartDate }: UseQueryResult<string | null, Error> =
    useQuery({
      queryKey: ['latestWeeklyReportDate', dentistId],
      queryFn: async () => {
        if (!dentistId) return null;
        return getLatestWeeklyReportDate(dentistId);
      },
      enabled: !!dentistId
    });

  const {
    data: startDates = [],
    isLoading: loadingStartDates,
    isFetched: loadedStartDates,
    refetch: fetchStartDates
  }: UseQueryResult<string[], Error> = useQuery({
    queryKey: ['weeklyReportDates', dentistId],
    queryFn: async () => {
      if (!dentistId) return [];
      const reportDates = await getWeeklyReportDates(dentistId);
      return reportDates
        .map((report) => report.start_date)
        .sort((a, b) => compareDesc(parseDate(a), parseDate(b)));
    },
    enabled: false
  });

  const {
    data: weeklyReport = null,
    isLoading: loading,
    isFetched: loaded
  }: UseQueryResult<WeeklyReportQueryResult | null, Error> = useQuery({
    queryKey: ['weeklyReport', dentistId, selectedStartDate],
    queryFn: async () => {
      if (!dentistId || !selectedStartDate) return null;

      const report = await queryClient.fetchQuery({
        queryKey: [
          'weeklyReportByDateAndDentistId',
          dentistId,
          selectedStartDate
        ],
        queryFn: () =>
          getWeeklyReportByDateAndDentistId(dentistId, selectedStartDate)
      });

      if (!report?.encoded_data) {
        displayToast({
          message: 'No weekly report found for the selected date.',
          duration: 3000,
          position: 'bottom'
        });
        return null;
      }

      const decodedData = await decodeWeeklyReportData(report.encoded_data);
      const dentistNames = Object.keys(
        decodedData.reportData?.appointmentsByDentist || {}
      );

      setSelectedDentist(WEEKLY_REPORT_DEFAULT_DENTIST);
      return {
        data: decodedData,
        dentists: [
          WEEKLY_REPORT_DEFAULT_DENTIST,
          ...dentistNames
            .sort()
            .filter((dentist) => dentist !== WEEKLY_REPORT_DEFAULT_DENTIST)
        ]
      };
    },
    enabled: !!dentistId && !!selectedStartDate
  });

  const appointmentsPerDay = useMemo(() => {
    if (
      !selectedDentist ||
      !weeklyReport?.data?.reportData?.appointmentsByDentist
    )
      return {};

    return (
      weeklyReport.data.reportData.appointmentsByDentist[selectedDentist] || {}
    );
  }, [weeklyReport, selectedDentist]);

  const chartData = useMemo(() => {
    const allDays = Array.from({ length: 7 }, (_, i) => {
      const date = addDays(startOfWeek(now()), i);
      return format(date, 'EEEE');
    });

    return allDays.map((day) => ({
      Day: day,
      Appointments: appointmentsPerDay[day] || 0
    }));
  }, [appointmentsPerDay]);

  const totalAppointments = useMemo(() => {
    return Object.values(appointmentsPerDay).reduce(
      (sum: number, appointments: number) => sum + appointments,
      0
    );
  }, [appointmentsPerDay]);

  const noShowRate = useMemo(() => {
    if (
      !selectedDentist ||
      !weeklyReport?.data?.reportData?.noShowRatesByDentist
    )
      return 0;

    if (selectedDentist === WEEKLY_REPORT_DEFAULT_DENTIST) {
      const noShowRatesByDentist =
        weeklyReport.data.reportData.noShowRatesByDentist;
      const totalNoShowRate = Object.values(noShowRatesByDentist).reduce(
        (sum: number, rate: number) => sum + (rate || 0),
        0
      );
      const count = Object.keys(noShowRatesByDentist).length;
      return count > 0 ? parseFloat((totalNoShowRate / count).toFixed(2)) : 0;
    }

    return parseFloat(
      (
        weeklyReport.data.reportData.noShowRatesByDentist[selectedDentist] || 0
      ).toFixed(2)
    );
  }, [weeklyReport, selectedDentist]);

  const noShowPatients = useMemo(() => {
    if (!selectedDentist) return [];

    const noShowPatientsByDentist =
      weeklyReport?.data?.reportData?.noShowPatientsByDentist || {};
    if (selectedDentist === WEEKLY_REPORT_DEFAULT_DENTIST) {
      return Object.values(noShowPatientsByDentist).flat();
    }

    return noShowPatientsByDentist[selectedDentist] || [];
  }, [weeklyReport, selectedDentist]);

  const lateRate = useMemo(() => {
    if (!selectedDentist) return 0;

    if (selectedDentist === WEEKLY_REPORT_DEFAULT_DENTIST) {
      const lateRatesByDentist =
        weeklyReport?.data?.reportData?.lateRatesByDentist || {};
      const totalLateRate = Object.values(lateRatesByDentist).reduce(
        (sum, rate) => sum + (rate || 0),
        0
      );
      const count = Object.keys(lateRatesByDentist).length;
      return count > 0 ? parseFloat((totalLateRate / count).toFixed(2)) : 0;
    }

    return parseFloat(
      (
        weeklyReport?.data?.reportData?.lateRatesByDentist?.[selectedDentist] ||
        0
      ).toFixed(2)
    );
  }, [weeklyReport, selectedDentist]);

  const latePatients = useMemo(() => {
    if (!selectedDentist) return [];

    const latePatientsByDentist =
      weeklyReport?.data?.reportData?.latePatientsByDentist || {};
    if (selectedDentist === WEEKLY_REPORT_DEFAULT_DENTIST) {
      return Object.values(latePatientsByDentist).flat();
    }

    return latePatientsByDentist[selectedDentist] || [];
  }, [weeklyReport, selectedDentist]);

  const { data: weeklyReportData, dentists } = weeklyReport ?? {};

  return (
    <WeeklyReportsContext.Provider
      value={{
        latestStartDate: latestStartDate ?? null,
        selectedStartDate,
        setSelectedStartDate,
        selectedDentist,
        setSelectedDentist,
        dentists: dentists ?? [],
        weeklyReportData: weeklyReportData ?? null,
        loading,
        loaded,
        appointmentsPerDay,
        totalAppointments,
        noShowRate,
        lateRate,
        startDates,
        loadingStartDates,
        loadedStartDates,
        fetchStartDates,
        chartData,
        noShowPatients,
        latePatients
      }}
    >
      {children}
    </WeeklyReportsContext.Provider>
  );
};

export const useWeeklyReports = () => {
  const context = useContext(WeeklyReportsContext);
  if (context === undefined) {
    throw new Error(
      'useWeeklyReports must be used within a WeeklyReportsProvider'
    );
  }
  return context;
};
